import { useState } from 'react';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CarrierDialog, VerticalStepper, removeEmptyStringKeys } from 'remote/global';
import {
  usePostOperatorsDriversMutation,
  usePostOperatorsFilesMutation,
} from 'api/queries/operators';
import { FormStep } from 'utils/data/data-types';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mui/system';
import { toast } from 'react-hot-toast';
import { runSequentially } from 'utils/helpers';
import { DriverDtoSchema } from '../BookingFormFactory/types';
import {
  DriverDetailsSchemaType,
  DriverDocumentsSchemaType,
  DriverImagesSchemaType,
  driverDetailsSchema,
  driverDocumentsSchema,
  driverImagesSchema,
} from './types';
import { DriverDetailsFormFields } from './DriverDetailsForm';
import { DriverImageFormFields } from './DriverImageForm';
import { DriverDocumentsFormFields } from './DriverDocumentsForm';

type VehicleCreateFormProps = {
  steps: FormStep[];
  activeStep: number;
  setActiveStep: (step: number) => void;
  onComplete: () => void;
};

type FormResult = [DriverDetailsSchemaType?, DriverImagesSchemaType?, DriverDocumentsSchemaType?];

function DriverDetailsStepForm({
  defaultValues,
  onValid,
}: {
  defaultValues?: DriverDetailsSchemaType;
  onValid: (data: DriverDetailsSchemaType) => void;
}) {
  const { t } = useTranslation();
  const formInstance = useForm<DriverDetailsSchemaType>({
    defaultValues: {
      contactInfo: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      },
      employmentContractNo: '',
      idCode: '',
      languages: [],
      ...defaultValues,
    },
    resolver: zodResolver(driverDetailsSchema),
  });
  const {
    handleSubmit,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <DriverDetailsFormFields form={formInstance} focusInitial />
      <Button
        type='submit'
        variant='contained'
        disabled={isSubmitting || isValidating || isLoading}
        fullWidth
        sx={{ mt: '2rem' }}
      >
        {t('continue')}
      </Button>
    </form>
  );
}

function DriverImagesStepForm({
  defaultValues,
  onValid,
  onBack,
}: {
  defaultValues?: DriverImagesSchemaType;
  onValid: (data: DriverImagesSchemaType) => void;
  onBack: (data: DriverImagesSchemaType) => void;
}) {
  const { t } = useTranslation();
  const formInstance = useForm<DriverImagesSchemaType>({
    defaultValues: {
      attachments: undefined,
      ...defaultValues,
    },
    resolver: zodResolver(driverImagesSchema),
  });
  const {
    handleSubmit,
    getValues,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <DriverImageFormFields form={formInstance} />
      <Stack direction='row' justifyContent='space-between' spacing={1} my='2rem'>
        <Button
          type='button'
          onClick={() => onBack(getValues())}
          variant='outlined'
          disabled={isSubmitting || isValidating || isLoading}
        >
          {t('back')}
        </Button>
        <Button
          type='submit'
          disabled={isSubmitting || isValidating || isLoading}
          variant='contained'
          sx={{ display: 'flex', maxWidth: 300 }}
          autoFocus
        >
          {t('continue')}
        </Button>
      </Stack>
    </form>
  );
}

function DriverDocumentsStepForm({
  defaultValues,
  onValid,
  onBack,
}: {
  defaultValues?: DriverDocumentsSchemaType;
  onValid: (data: DriverDocumentsSchemaType) => void;
  onBack: (data: DriverDocumentsSchemaType) => void;
}) {
  const { t } = useTranslation();
  const formInstance = useForm<DriverDocumentsSchemaType>({
    defaultValues: {
      driverCertifications: undefined,
      driverLicenses: undefined,
      ...defaultValues,
    },
    resolver: zodResolver(driverDocumentsSchema),
  });
  const {
    handleSubmit,
    getValues,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <DriverDocumentsFormFields form={formInstance} />
      <Stack direction='row' justifyContent='space-between' spacing={1} my='2rem'>
        <Button
          type='button'
          onClick={() => onBack(getValues())}
          variant='outlined'
          disabled={isSubmitting || isValidating || isLoading}
        >
          {t('back')}
        </Button>
        <Button
          type='submit'
          disabled={isSubmitting || isValidating || isLoading}
          variant='contained'
          sx={{ display: 'flex', maxWidth: 300 }}
          autoFocus
        >
          {t('confirm')}
        </Button>
      </Stack>
    </form>
  );
}

function DriverCreateForm({
  steps,
  activeStep,
  setActiveStep,
  onComplete,
}: VehicleCreateFormProps) {
  const { t } = useTranslation();
  const [formResult, setFormResult] = useState<FormResult>();
  const [driverDetailsValues, driverImagesValues, driverDocumentsValues] = formResult ?? [];
  const postOperatorsDriversMutation = usePostOperatorsDriversMutation();
  const postOperatorsFilesMutation = usePostOperatorsFilesMutation();

  const setFormResultForStep = (step: number, data: any) => {
    setFormResult((prevState) => {
      const newState = prevState ?? [];
      newState[step] = data;
      return newState;
    });
  };

  const handleGoBack = (data: any) => {
    setFormResultForStep(activeStep, data);
    setActiveStep(activeStep - 1);
  };

  const handleValidStep = async (data: any) => {
    const nextStepIndex = activeStep + 1;
    // If is not the last step
    if (nextStepIndex < steps.length) {
      setFormResultForStep(activeStep, data);
      setActiveStep(nextStepIndex);
      return;
    }

    const payload: DriverDtoSchema = removeEmptyStringKeys({
      ...driverDetailsValues!,
    });

    const response = await postOperatorsDriversMutation.mutateAsync(payload);
    const driverId = response.id;

    const files = [
      ...(driverImagesValues?.attachments ?? []),
      ...(driverDocumentsValues?.driverCertifications ?? []),
      ...(driverDocumentsValues?.driverLicenses ?? []),
      // Data holds the last steps data, hence in this case the documents
      ...(data?.driverCertifications ?? []),
      ...(data?.driverLicenses ?? []),
    ];

    const promises = files.map((file) => () => {
      const formData = new FormData();
      formData.append('file', file.originalFile as File);
      formData.append('fileType', file.fileType);
      formData.append('referenceId', String(driverId));
      return postOperatorsFilesMutation.mutateAsync(formData);
    });

    // Call the promise functions sequentially
    await runSequentially(promises);
    toast.success(t('Toast.success'));
    onComplete();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        justifyContent: 'center',
        mt: [null, '3rem'],
        mb: ['3rem', '6rem'],
        maxWidth: '1500px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          px: '1rem',
          maxWidth: '400px',
          width: '100%',
        }}
      >
        {activeStep === 0 && (
          <DriverDetailsStepForm defaultValues={driverDetailsValues} onValid={handleValidStep} />
        )}
        {activeStep === 1 && (
          <DriverImagesStepForm
            defaultValues={driverImagesValues}
            onValid={handleValidStep}
            onBack={handleGoBack}
          />
        )}
        {activeStep === 2 && (
          <DriverDocumentsStepForm
            defaultValues={driverDocumentsValues}
            onValid={handleValidStep}
            onBack={handleGoBack}
          />
        )}
      </Box>
    </Box>
  );
}

interface Props {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function DriverCreateModalForm({ isModalOpen, setIsModalOpen }: Props) {
  const [activeStep, setActiveStep] = useState(0);
  const { t } = useTranslation();

  const handleClose = () => {
    setIsModalOpen(false);
    setActiveStep(0);
  };

  const steps: FormStep[] = [
    {
      label: t('modals.createDriver.steps.driverDetails.title'),
      description: '',
    },
    {
      label: t('modals.createDriver.steps.driverImages.title'),
      description: t('modals.createDriver.steps.driverImages.subtitle'),
    },
    {
      label: t('modals.createDriver.steps.driverDocuments.title'),
      description: t('modals.createDriver.steps.driverDocuments.subtitle'),
    },
  ];

  return (
    <CarrierDialog
      open={isModalOpen}
      setOpen={setIsModalOpen}
      onClose={handleClose}
      title={t('modals.createDriver.title').toString()}
      preventCloseOutside
    >
      <VerticalStepper activeStep={activeStep} steps={steps} />
      <DriverCreateForm
        steps={steps}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        onComplete={handleClose}
      />
    </CarrierDialog>
  );
}

export default DriverCreateModalForm;
